import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Table,
  Space,
  Modal,
  message,
  Card,
  Input,
  Select,
  DatePicker,
  Tag,
  Row,
  Col,
  Typography,
} from "antd";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  SyncOutlined
} from "@ant-design/icons";
import moment from "moment";
import CreateLawyerModal from "../components/CreateLawyerModal";
import EditLawyerModal from "../components/EditLawyerModal";
import { fetchAllLawyers, deleteLawyerById } from "../../../api/lawyerApi";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

export default function LawyersPage() {
  const [lawyers, setLawyers] = useState([]);
  const [filteredLawyers, setFilteredLawyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLawyer, setEditingLawyer] = useState(null);
  const [filterParams, setFilterParams] = useState({
    search: '',
    status: '',
    gender: '',
    dateRange: []
  });

  const role = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.role || "";
    } catch {
      return "";
    }
  }, []);


  const statusOptions = ['Active', 'Inactive', 'Pending'];
  const genderOptions = ['Male', 'Female', 'Other'];

  const fetchLawyers = async () => {
    setLoading(true);
    try {
      const data = await fetchAllLawyers();
      setLawyers(data);
      setFilteredLawyers(data);
    } catch (error) {
      message.error("Failed to fetch lawyers");
    } finally {
      setLoading(false);
    }
  };

  const deleteLawyer = async (id) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: "Are you sure you want to delete this lawyer? This action cannot be undone.",
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteLawyerById(id);
          toastSuccess("Lawyer deleted successfully");
          fetchLawyers();
        } catch (error) {
          console.error("Delete error:", error.response || error.message);
          toastError("Failed to delete lawyer");
        }
      },
    });
  };

  const handleFilterChange = (name, value) => {
    setFilterParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    let result = [...lawyers];

    // Search filter (name, email, mobile)
    if (filterParams.search) {
      const searchTerm = filterParams.search.toLowerCase();
      result = result.filter(lawyer =>
        lawyer.name.toLowerCase().includes(searchTerm) ||
        lawyer.email.toLowerCase().includes(searchTerm) ||
        lawyer.mobile.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (filterParams.status) {
      result = result.filter(lawyer => lawyer.status === filterParams.status);
    }

    // Gender filter
    if (filterParams.gender) {
      result = result.filter(lawyer => lawyer.gender === filterParams.gender);
    }

    // Date range filter
    if (filterParams.dateRange && filterParams.dateRange.length === 2) {
      const [start, end] = filterParams.dateRange;
      result = result.filter(lawyer => {
        const createdAt = moment(lawyer.created_at);
        return createdAt.isBetween(start, end, 'day', '[]');
      });
    }

    setFilteredLawyers(result);
  };

  const resetFilters = () => {
    setFilterParams({
      search: '',
      status: '',
      gender: '',
      dateRange: []
    });
    setFilteredLawyers(lawyers);
  };

  useEffect(() => {
    fetchLawyers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filterParams, lawyers]);


  const columns = useMemo(() => {
    const base = [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (text) => <Text strong>{text}</Text>
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        render: (email) => <Text type="secondary">{email}</Text>
      },
      {
        title: "Mobile",
        dataIndex: "mobile",
        key: "mobile"
      },
      {
        title: "Gender",
        dataIndex: "gender",
        key: "gender",
        render: (gender) => (
          <Tag color={gender === 'Male' ? 'blue' : gender === 'Female' ? 'pink' : 'purple'}>
            {gender}
          </Tag>
        )
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => (
          <Tag
            color={
              status === 'Active' ? 'green' :
                status === 'Inactive' ? 'red' : 'orange'
            }
          >
            {status}
          </Tag>
        ),
        filters: statusOptions.map(status => ({ text: status, value: status })),
        onFilter: (value, record) => record.status === value,
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        render: (date) => moment(date).format("YYYY-MM-DD HH:mm"),
        sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
      },
    ];

    if (role !== "firm") {
      base.push({
        title: "Actions",
        key: "actions",
        fixed: 'right',
        width: 120,
        render: (_, record) => (
          <Space size="small">
            <Button
              icon={<EditOutlined />}
              type="text"
              size="small"
              onClick={() => setEditingLawyer(record)}
            />
            <Button
              icon={<DeleteOutlined />}
              type="text"
              danger
              size="small"
              onClick={() => deleteLawyer(record.user_id)}
            />
          </Space>
        ),
      });
    }

    return base;
  }, [role]);
  return (
    <div style={{ padding: '16px' }}>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>Lawyers Management</Title>
            {role !== "firm" && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowCreateModal(true)}
              >
                Add Lawyer
              </Button>
            )}
          </div>
        }
        bordered={false}
        style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)' }}
      >
        {/* Filter Section */}
        <Card
          style={{ marginBottom: 24 }}
          bordered={false}
          bodyStyle={{ padding: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                placeholder="Search by name, email or phone"
                prefix={<SearchOutlined />}
                value={filterParams.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by status"
                style={{ width: '100%' }}
                value={filterParams.status || ''}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
              >
                {statusOptions.map(status => (
                  <Option key={status} value={status}>{status}</Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by gender"
                style={{ width: '100%' }}
                value={filterParams.gender || ''}
                onChange={(value) => handleFilterChange('gender', value)}
                allowClear
              >
                {genderOptions.map(gender => (
                  <Option key={gender} value={gender}>{gender}</Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                style={{ width: '100%' }}
                value={filterParams.dateRange}
                onChange={(dates) => handleFilterChange('dateRange', dates)}
              />
            </Col>

            <Col xs={24} style={{ textAlign: 'right' }}>
              <Space>
                <Button
                  icon={<FilterOutlined />}
                  onClick={applyFilters}
                  type="primary"
                  ghost
                >
                  Apply Filters
                </Button>
                <Button
                  icon={<SyncOutlined />}
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Table
          rowKey="user_id"
          columns={columns}
          dataSource={filteredLawyers}
          loading={loading}
          bordered
          scroll={{ x: true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} lawyers`
          }}
        />
      </Card>

      <CreateLawyerModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          fetchLawyers();
          setShowCreateModal(false);
        }}
      />

      <EditLawyerModal
        open={!!editingLawyer}
        onClose={() => setEditingLawyer(null)}
        lawyer={editingLawyer}
        onSuccess={() => {
          fetchLawyers();
          setEditingLawyer(null);
        }}
      />
    </div>
  );
}